import React, { useEffect, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import { useTranslation } from 'react-i18next';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { FilterChip, SearchInput } from '../shared/ListFilters';
import DataTable from '../shared/DataTable';
import { numFont } from '../../utils/fonts';
import UserDetailDialog from './UserDetailDialog';

const FILTERS = {
  active: { is_active: 'true' },
  inactive: { is_active: 'false' },
  staff: { is_staff: 'true' },
  no_org: { no_organization: 'true' },
};

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' }) : '';
}

/** Every Verstaani user across all clients. A row opens the user's details, where staff can
 * generate a new random password for the user. */
export default function UsersTab() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detailUserId, setDetailUserId] = useState(null);

  const {
    data, loading, page, pageSize, totalCount, totalPages, setPage, setPageSize, setSearchQuery, setFilters,
    sortField, sortDirection, handleSort, refetch,
  } = usePaginatedList('/api/vadmin/users/', {
    initialFilters: {},
    defaultSortField: 'date_joined',
    defaultSortDirection: 'desc',
    defaultPageSize: 50,
  });

  useEffect(() => {
    setFilters(FILTERS[filter] || {});
  }, [filter, setFilters]);

  const columns = [
    {
      key: 'name', label: t('vadmin.usersTab.name'), sortable: true, maxWidth: 220,
      render: (r) => r.name || <Typography component="span" variant="body2" color="text.disabled">-</Typography>,
    },
    { key: 'email', label: t('vadmin.usersTab.email'), sortable: true, maxWidth: 260, render: (r) => r.email },
    {
      key: 'organization_names', label: t('vadmin.usersTab.organizations'), maxWidth: 320, hideBelow: 'md',
      render: (r) => (r.organization_names.length
        ? r.organization_names.join(', ')
        : <Typography component="span" variant="body2" color="text.disabled">{t('vadmin.usersTab.no_organizations')}</Typography>),
    },
    {
      key: 'last_login', label: t('vadmin.usersTab.last_login'), sortable: true, width: '1%', noWrap: true, hideBelow: 'sm',
      render: (r) => <Box component="span" sx={numFont}>{r.last_login ? formatDateTime(r.last_login) : t('vadmin.never')}</Box>,
    },
    {
      key: 'date_joined', label: t('vadmin.usersTab.joined'), sortable: true, width: '1%', noWrap: true, hideBelow: 'lg',
      render: (r) => <Box component="span" sx={numFont}>{formatDateTime(r.date_joined)}</Box>,
    },
    {
      key: 'status', label: t('vadmin.usersTab.status'), width: '1%',
      render: (r) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'nowrap' }}>
          {!r.is_active && <Chip size="small" color="error" label={t('vadmin.usersTab.inactive')} />}
          {r.is_superuser
            ? <Chip size="small" color="warning" label={t('vadmin.usersTab.superuser')} />
            : r.is_staff && <Chip size="small" color="warning" variant="outlined" label={t('vadmin.usersTab.staff')} />}
          {r.mfa_enabled && <Chip size="small" color="success" variant="outlined" label={t('vadmin.usersTab.mfa')} />}
        </Stack>
      ),
    },
  ];

  return (
    <Box data-testid="vadmin-users-tab">
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <SearchInput
          value={search}
          onChange={(value) => { setSearch(value); setSearchQuery(value); }}
          placeholder={t('vadmin.usersTab.search')}
        />
        {Object.keys(FILTERS).map((key) => (
          <FilterChip
            key={key}
            label={t(`vadmin.usersTab.filter_${key}`)}
            active={filter === key}
            onClick={() => setFilter(filter === key ? '' : key)}
          />
        ))}
      </Stack>
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        onRowClick={(row) => setDetailUserId(row.id)}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyIcon={<PeopleIcon />}
        emptyTitle={t('vadmin.usersTab.empty')}
        emptyDescription={t('vadmin.usersTab.empty_help')}
        pagination={{ page, totalPages, totalCount, pageSize, onPageChange: setPage, onPageSizeChange: setPageSize }}
      />
      <UserDetailDialog
        open={Boolean(detailUserId)}
        userId={detailUserId}
        onClose={() => setDetailUserId(null)}
        onChanged={refetch}
      />
    </Box>
  );
}
